/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency, formatDateOnly } from '../utils/formatters.ts';
import './RecentSalesList.css';

export interface RecentSaleEntry {
  orderId: string;
  orderNumber: string;
  createdAt: string;
  totalPrice: number;
  financialStatus: string;
}

interface RecentSalesListProps {
  sales: RecentSaleEntry[];
  loading?: boolean;
}

const RecentSalesList: React.FC<RecentSalesListProps> = ({ sales, loading = false }) => {
  return (
    <div className="recent-sales">
      <div className="recent-sales__header">
        <h3>Last 20 Sales</h3>
      </div>

      {loading ? (
        <div className="recent-sales__empty">Loading&hellip;</div>
      ) : sales.length === 0 ? (
        <div className="recent-sales__empty">No orders yet in this window.</div>
      ) : (
        <ul className="recent-sales__list">
          {sales.map((sale) => {
            const isRefunded = sale.financialStatus?.toLowerCase() === 'refunded';
            return (
              <li key={sale.orderId} className="recent-sales__row">
                <span className="recent-sales__date">{formatDateOnly(sale.createdAt)}</span>
                <span className="recent-sales__order-pill">#{sale.orderNumber}</span>
                {isRefunded && (
                  <span className="recent-sales__badge recent-sales__badge--refunded">
                    &#9888; Refunded
                  </span>
                )}
                <span className="recent-sales__amount">{formatCurrency(sale.totalPrice)}</span>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && (
        <Link to="/dashboard" className="recent-sales__see-all">
          See All Sales
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>
      )}
    </div>
  );
};

export default RecentSalesList;
